import React, { ReactNode } from 'react';
import moment from 'moment';
import { Badge } from 'antd';
import classNames from 'classnames';
import UserAvatar from '@components/UserAvatar';
import { TaskEventDto } from '@api/Api';
import { htmlSanitize } from '@helpers/htmlSanitize';
import {
  TaskActivityCreatedIcon,
  TaskActivityDeleteIcon,
  TaskActivityDueDateIcon,
  TaskActivityDueDateUpdateIcon,
  TaskActivityLinkAssetIcon,
  TaskActivitySetDescriptionIcon,
  TimelineCheckIcon,
  TaskActivitySetPriorityIcon,
  TaskActivityUploadFileIcon,
  TaskActivityWriteIcon
} from '@icons';
import './Activity.less';

interface IActivityProps {
  isLastActivity: boolean;
  activity: TaskEventDto;
}

function Activity({ activity, isLastActivity }: IActivityProps) {
  interface ICommentActivityItemProps {
    user: any;
    eventTime: Date;
    newActivityText: string;
    activityIcon: ReactNode | null;
  }

  interface IDefaultActivityItemProps {
    user: any;
    eventTime: Date;
    actionText: string;
    prevActivityText: string;
    intermediateText: string;
    newActivityText: string;
    activityIcon: ReactNode | null;
    isRichText?: boolean;
  }

  function defaultActivityItem({
    user = null,
    eventTime,
    actionText = '',
    prevActivityText = '',
    intermediateText = '',
    newActivityText = '',
    activityIcon = null,
    isRichText = false
  }: IDefaultActivityItemProps) {
    return (
      <>
        <div className="activity-container">
          <div className={classNames(!isLastActivity && 'activity-avatar')}>
            <Badge count={activityIcon} offset={[-7, 31]}>
              <UserAvatar
                isActive={true}
                size="large"
                src={user?.picture?.url || ''}
                userEmail={user?.email || ''}
                userName={user?.name ?? 'Deleted User'}
              />
            </Badge>
          </div>
          <span className="activity-name">
            <span style={{ fontWeight: 400 }}>
              {user?.me ? 'You' : user?.name ?? 'Deleted User'} {actionText}{' '}
            </span>
            {!isRichText && (
              <span style={{ fontWeight: 700 }}>{prevActivityText}</span>
            )}
            {isRichText && (
              <div
                className="default-rich-text"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: htmlSanitize(prevActivityText) as any
                }}
              />
            )}
            {prevActivityText && (
              <span style={{ fontWeight: 400 }}>{intermediateText}</span>
            )}
            {!isRichText && (
              <span style={{ fontWeight: 700 }}>{newActivityText}</span>
            )}
            {isRichText && (
              <div
                className="default-rich-text"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: htmlSanitize(newActivityText) as any
                }}
              />
            )}
          </span>
          <span className="activity-time">
            {moment(eventTime).format('MMM DD, h:mm a')}
          </span>
        </div>
        {/* {!isLastActivity && <div className="activity-line" />} */}
      </>
    );
  }

  function commentActivityItem({
    user = null,
    eventTime,
    newActivityText = '',
    activityIcon = null
  }: ICommentActivityItemProps) {
    return (
      <>
        <div className="comment-activity-container">
          {defaultActivityItem({
            user,
            eventTime,
            actionText: '',
            prevActivityText: 'left a new comment to this task',
            intermediateText: '',
            newActivityText: '',
            activityIcon
          })}
          <div
            className="comment-text"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: htmlSanitize(newActivityText) as any
            }}
          />
          <div className="activity-line comment-activity-line" />
        </div>
        {!isLastActivity && <div className="activity-line" />}
      </>
    );
  }

  const changeDateFormat = (date: any) =>
    moment(date).minutes() === 59
      ? moment(date).format('MMM DD')
      : moment(date).format('MMM DD, h:mm a');

  const activitySelector = (activity: any) => {
    const { user, eventTime, payload } = activity;

    switch (activity.payload.type) {
      case 'created':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: 'created task ',
          prevActivityText: '',
          intermediateText: '',
          newActivityText: payload.name,
          activityIcon: (
            <TaskActivityCreatedIcon className="timeline-badge timeline-badge--blue" />
          )
        });
      case 'status_changed': {
        const prevStatusText =
          payload.prevStatus === 'in_progress'
            ? 'In progress'
            : payload.prevStatus === 'done'
            ? 'Done'
            : payload.prevStatus === 'todo'
            ? 'To do'
            : payload.prevStatus === 'stuck'
            ? 'Blocked'
            : payload.prevStatus;

        const newStatusText =
          payload.newStatus === 'in_progress'
            ? 'In progress'
            : payload.newStatus === 'done'
            ? 'Done'
            : payload.newStatus === 'todo'
            ? 'To do'
            : payload.newStatus === 'stuck'
            ? 'Blocked'
            : payload.newStatus;

        return defaultActivityItem({
          user,
          eventTime,
          actionText: payload.prevStatus
            ? 'updated task status from '
            : 'set status ',
          prevActivityText: payload.prevStatus ? prevStatusText : '',
          intermediateText: ' to ',
          newActivityText: newStatusText,
          activityIcon: (
            <TaskActivityWriteIcon className="timeline-badge timeline-badge--muted" />
          )
        });
      }
      case 'priority_changed':
        return defaultActivityItem({
          user,
          eventTime,
          actionText:
            payload.newPriority !== null ? 'set priority ' : 'delete priority',
          prevActivityText: '',
          intermediateText: '',
          newActivityText: payload.newPriority
            ? payload.newPriority.charAt(0).toUpperCase() +
              payload.newPriority.slice(1)
            : '',
          activityIcon: payload.newPriority ? (
            <TaskActivitySetPriorityIcon className="timeline-badge timeline-badge--success" />
          ) : (
            <TaskActivityDeleteIcon className="timeline-badge timeline-badge--muted" />
          )
        });
      case 'description_changed':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: payload.prevDescription
            ? 'update task description '
            : 'set task description to ',
          prevActivityText: payload.prevDescription
            ? payload.prevDescription
            : '',
          intermediateText: ' to ',
          newActivityText: payload.newDescription,
          activityIcon: payload.prevDescription ? (
            <TaskActivityWriteIcon className="timeline-badge timeline-badge--muted" />
          ) : (
            <TaskActivitySetDescriptionIcon className="timeline-badge timeline-badge--dark" />
          ),
          isRichText: true
        });
      case 'comment':
        return commentActivityItem({
          user,
          eventTime,
          newActivityText: payload.text,
          activityIcon: (
            <TaskActivityDueDateIcon className="timeline-badge timeline-badge--dark" />
          )
        });
      case 'renamed':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: 'renamed task ',
          prevActivityText: payload.prevName,
          intermediateText: ' to ',
          newActivityText: payload.newName,
          activityIcon: (
            <TaskActivityWriteIcon className="timeline-badge timeline-badge--muted" />
          )
        });
      case 'due_date_changed':
        return defaultActivityItem({
          user,
          eventTime,
          actionText:
            payload.prevDueDate && payload.newDueDate
              ? 'updated due date from '
              : payload.newDueDate
              ? 'set due date '
              : 'unset due date ',
          prevActivityText: payload.prevDueDate
            ? changeDateFormat(payload.prevDueDate)
            : '',
          intermediateText: payload.newDueDate ? ' to ' : '',
          newActivityText: payload.newDueDate
            ? changeDateFormat(payload.newDueDate)
            : '',
          activityIcon: payload.prevDueDate ? (
            <TaskActivityDueDateUpdateIcon className="timeline-badge timeline-badge--muted" />
          ) : (
            <TaskActivityDueDateIcon className="timeline-badge timeline-badge--success" />
          )
        });
      case 'board_changed':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: payload.prevBoard
            ? 'updated task group name from '
            : 'set task group name to ',
          prevActivityText: payload.prevBoard
            ? payload.prevBoard.id === 'no_assigned'
              ? 'No task group assigned'
              : payload.prevBoard.name
            : '',
          intermediateText: ' to ',
          newActivityText:
            payload.newBoard.id === 'no_assigned'
              ? 'No task group assigned'
              : payload.newBoard.name,
          activityIcon: payload.prevBoard ? (
            <TaskActivityWriteIcon className="timeline-badge timeline-badge--muted" />
          ) : (
            <TimelineCheckIcon className="timeline-badge timeline-badge--dark" />
          )
        });
      case 'attachment_created':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: 'attached file ',
          newActivityText: payload.attachment.name,
          prevActivityText: '',
          intermediateText: '',
          activityIcon: (
            <TaskActivityUploadFileIcon className="timeline-badge timeline-badge--muted" />
          )
        });
      case 'attachment_deleted':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: 'deleted file ',
          newActivityText: payload.attachment.name,
          prevActivityText: '',
          intermediateText: '',
          activityIcon: (
            <TaskActivityDeleteIcon className="timeline-badge timeline-badge--muted" />
          )
        });
      case 'asset_version_changed':
        return defaultActivityItem({
          user,
          eventTime,
          actionText:
            payload.newAssetVersion !== null
              ? 'linked media '
              : 'deleted media ',
          newActivityText:
            payload.newAssetVersion !== null
              ? `${payload.newAssetVersion.name}.${payload.newAssetVersion.extension}`
              : `${payload.prevAssetVersion.name}.${payload.prevAssetVersion.extension}`,
          prevActivityText: '',
          intermediateText: '',
          activityIcon:
            payload.newAssetVersion !== null ? (
              <TaskActivityLinkAssetIcon className="timeline-badge timeline-badge--dark" />
            ) : (
              <TaskActivityDeleteIcon className="timeline-badge timeline-badge--muted" />
            )
        });
      case 'deleted':
        return defaultActivityItem({
          user,
          eventTime,
          actionText: 'deleted task',
          newActivityText: payload.name,
          prevActivityText: '',
          intermediateText: '',
          activityIcon: (
            <TaskActivityDeleteIcon className="timeline-badge timeline-badge--muted" />
          )
        });
      default:
        return null;
    }
  };

  return <div>{activitySelector(activity)}</div>;
}

export default Activity;
